/**
 * What a run earned (K3 of the seat-screens sketch): the state of a finished run against what
 * the run started with. Readings only: no rule reads them and the state keeps neither, so the
 * outer layers take the start off a fresh run and keep it beside the run they save.
 */
import { ownSeat, playerSeat } from "./command";
import { optionalField, positiveField } from "./fields";
import { workingCities } from "./land/cities";
import { landCount } from "./run/board";
import { owedOf } from "./soldier/prospect";
import type { RunState } from "./types/state";

export interface RunStart {
  /**
   * The account's purse and renown as the run began, filled from the save (VC: a run starts with
   * none in hand); absent for a seat that has none.
   */
  readonly purse?: number;
  readonly renown?: number;
  /** The hexes and the working cities of the seat's kingdom as the run began. */
  readonly land: number;
  readonly cities: number;
}

export interface RunEarned {
  /**
   * What the run earned: its coins, kept or spent (VC), and its renown; absent for a seat that has
   * none.
   */
  readonly purse?: number;
  readonly renown?: number;
  /** What the King still owes the seat: the run's, so lost with it. Absent while nothing is owed. */
  readonly unpaid?: number;
  /** The kingdom's land and working cities over the start's; under 0 for a kingdom that shrank. */
  readonly land: number;
  readonly cities: number;
}

/** The account a run starts from (VC): the bank and the renown the save holds. */
export interface Account {
  readonly purse: number;
  readonly renown: number;
}

/**
 * A run's start (K3): its kingdom's land and cities now, and for a seat that has a purse and
 * renown the account's (VC: filled from the save, since the run holds none in hand; 0 without one).
 * Taken off a fresh run, it is the run's start.
 */
export function runStartOf(state: RunState, account?: Account): RunStart {
  const faction = playerSeat(state).faction;
  const seat = ownSeat(state);
  return {
    ...(seat?.purse === undefined ? {} : { purse: account?.purse ?? 0 }),
    ...(seat?.renown === undefined ? {} : { renown: account?.renown ?? 0 }),
    land: landCount(state, faction),
    cities: workingCities(state, faction).length,
  };
}

/**
 * What the run earned against its start (K3): the coins it holds and spent (VC), its renown (the
 * run's own, from 0), what the King still owes it (his pay and head money), and the kingdom's land
 * and cities over the start's.
 */
export function runEarned(start: RunStart, state: RunState): RunEarned {
  const faction = playerSeat(state).faction;
  const seat = ownSeat(state);
  const unpaid = owedOf(seat) + (seat?.headMoneyOwed ?? 0);
  return {
    ...optionalField(
      "purse",
      seat?.purse === undefined ? undefined : seat.purse + (seat.spent ?? 0),
    ),
    ...optionalField("renown", seat?.renown),
    ...positiveField("unpaid", unpaid),
    land: landCount(state, faction) - start.land,
    cities: workingCities(state, faction).length - start.cities,
  };
}

/**
 * The account after the run as it stands (VC): the start's coins plus the run's in hand, and its
 * renown likewise for a seat that earns renown (absent for one that does not); undefined for a
 * seat without a purse.
 */
export function bankedAccount(
  start: RunStart,
  state: RunState,
): { readonly purse: number; readonly renown?: number } | undefined {
  const seat = ownSeat(state);
  if (start.purse === undefined || seat?.purse === undefined) {
    return undefined;
  }
  return {
    purse: start.purse + seat.purse,
    ...optionalField(
      "renown",
      start.renown === undefined || seat.renown === undefined
        ? undefined
        : start.renown + seat.renown,
    ),
  };
}
